import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface PlanEpic {
  name: string;
  description: string;
  phase: string;
  issues: PlanIssue[];
}

export interface PlanIssue {
  title: string;
  description: string;
  labels: string[];
  dependencies: string[];
  acceptanceCriteria: string[];
}

export interface GeneratedPlan {
  productName: string;
  epics: PlanEpic[];
  phases: { name: string; epics: string[]; milestone: string }[];
}

export interface PlanStatusCounts {
  todo: number;
  inProgress: number;
  done: number;
}

export interface PlanningProject {
  id: string;
  number: number;
  title: string;
  closed: boolean;
  description: string;
  url: string;
  itemCount: number;
  /** Bucketed from each item's Status field (first 100 items). */
  statusCounts?: PlanStatusCounts;
}

export interface ProjectProgress {
  total: number;
  done: number;
  percentage: number;
}

export interface ProjectItem {
  id: string;
  number: number;
  title: string;
  body: string;
  state: string;
  url: string;
  repo: string;
  labels: string[];
  assignees: string[];
  fields: Record<string, string>;
}

export interface ProjectItemsResponse {
  items: ProjectItem[];
  projectTitle: string;
}

export const planningApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async generatePlan(
    description: string,
    context?: {
      techStack?: { name: string; version?: string; category: string; notes?: string }[];
      guardrails?: { title: string; description: string; category: string; severity: string }[];
      org?: string;
      repo?: string;
      planningContext?: {
        repoSummaries?: { repo: string; purpose: string; summary: string; conventions?: string[]; claudeMd?: string }[];
        dependencies?: { from: string; to: string; relationship: string }[];
        adrs?: { title: string; repo: string }[];
        specs?: { name: string; status: string }[];
        targetRepo?: string | null;
      };
    },
  ): Promise<GeneratedPlan> {
    // Start async job
    const { data: job } = await api.post('/planning/generate', { description, context });
    const jobId = job.jobId;

    // Poll until complete (max ~6 minutes — must outlast the backend's
    // 300s Lambda budget so a slow-but-successful generation isn't
    // reported as a timeout)
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: status } = await api.get(`/planning/jobs/${jobId}`);
      if (status.status === 'completed') return status.result;
      if (status.status === 'failed') throw new Error(status.error || 'Plan generation failed');
    }
    throw new Error('Plan generation timed out');
  },

  async listProjects(): Promise<PlanningProject[]> {
    const { data } = await api.get('/planning/projects');
    return data;
  },

  async createProject(title: string): Promise<{ title: string; number: number; url: string; org: string }> {
    const { data } = await api.post('/planning/projects', { title });
    return data;
  },

  async bulkCreateIssues(
    projectNumber: string,
    repo: string,
    epics: PlanEpic[],
  ): Promise<{ issuesCreated: number; issues: { number: number; title: string; url: string; epic: string }[] }> {
    const { data: job } = await api.post(`/planning/projects/${projectNumber}/issues/bulk`, { repo, epics });
    const jobId = job.jobId;

    // Poll until complete (max ~6 minutes — matches the backend's 300s
    // Lambda budget plus margin)
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: status } = await api.get(`/planning/jobs/${jobId}`);
      if (status.status === 'completed') return status.result;
      if (status.status === 'failed') throw new Error(status.error || 'Issue creation failed');
    }
    throw new Error('Issue creation timed out');
  },

  async getProgress(projectNumber: number): Promise<ProjectProgress> {
    const { data } = await api.get(`/planning/projects/${projectNumber}/progress`);
    return data;
  },

  async listProjectItems(projectNumber: number): Promise<ProjectItemsResponse> {
    const { data } = await api.get(`/planning/projects/${projectNumber}/items`);
    return data;
  },
};
